import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CommercePilotPanel } from "./CommercePilotPanel";
import { DutsCatalogPanel } from "./DutsCatalogPanel";

const apiUrl = import.meta.env.VITE_API_URL ?? "http://localhost:4000/v1";
const TOKEN_KEY = "gigflow_admin_token";

interface Overview {
  users: number;
  workers: number;
  pendingWorkers: number;
  openGigs: number;
  completedGigs: number;
  grossVolumeCents: number;
  platformRevenueCents: number;
  commissionRate: number;
  cancellationFeePercent?: number;
  cancellationGraceMinutes?: number;
}

interface PendingWorker {
  id: string;
  email: string;
  fullName: string;
  phoneNumber: string | null;
  avatarUrl?: string | null;
  accountStatus: string;
  city?: string | null;
  region?: string | null;
  createdAt: string;
  workerProfile: {
    city: string | null;
    serviceArea: string | null;
    serviceCategories: Array<{ id: string; name: string }>;
    governmentIdType?: string | null;
    identityVerificationStatus?: string | null;
  } | null;
  identity?: {
    profilePhotoUrl: string | null;
    governmentIdType: string | null;
    governmentIdFrontUrl: string | null;
    governmentIdBackUrl: string | null;
    uploadStatus: string;
    verificationStatus: string;
    documentsUploadedAt: string | null;
  };
}

interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  roles: string[];
  accountStatus: string;
  createdAt: string;
}

interface AdminGig {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  client?: { fullName: string };
}

type AdminTab = "overview" | "pending" | "users" | "gigs" | "commerce";
type CommerceSubTab = "merchants" | "orders" | "catalog" | "deliveries" | "unlisted";

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const response = await fetch(`${apiUrl}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    }
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
      message?: string;
      code?: string;
      errors?: Record<string, string>;
    } | null;
    const fieldMessage = body?.errors ? Object.values(body.errors).filter(Boolean).join(". ") : null;
    throw new Error(fieldMessage || body?.message || body?.error || `Request failed (${response.status})`);
  }

  return response.json() as Promise<T>;
}

function LoginPanel({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState("info@duts.tech");
  const [password, setPassword] = useState("");

  const loginMutation = useMutation({
    mutationFn: () =>
      fetch(`${apiUrl}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      }).then(async (response) => {
        if (!response.ok) {
          const body = (await response.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error ?? "Login failed");
        }
        const session = (await response.json()) as { token: string; user: { roles: string[] } };
        if (!session.user.roles.includes("ADMIN")) {
          throw new Error("This account is not an admin.");
        }
        return session;
      }),
    onSuccess: ({ token }) => {
      localStorage.setItem(TOKEN_KEY, token);
      onSuccess();
    }
  });

  return (
    <section className="login">
      <img className="login-logo" src="/logo.png" alt="DUTS" />
      <p className="eyebrow">Admin access</p>
      <h1>Sign in to DUTS Ops</h1>
      <p className="notice">Use info@duts.tech with the password from ADMIN_SEED_PASSWORD.</p>
      <label>
        Email
        <input
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </label>
      <label>
        Password
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </label>
      <button type="button" onClick={() => loginMutation.mutate()} disabled={loginMutation.isPending}>
        {loginMutation.isPending ? "Signing in..." : "Sign in"}
      </button>
      {loginMutation.error ? <p className="notice">{loginMutation.error.message}</p> : null}
    </section>
  );
}

const cards = [
  ["Users", "users"],
  ["Couriers", "workers"],
  ["Pending approvals", "pendingWorkers"],
  ["Open jobs", "openGigs"],
  ["Completed jobs", "completedGigs"],
  ["Gross volume", "grossVolumeCents"],
  ["Platform revenue", "platformRevenueCents"]
] as const;

function formatValue(key: keyof Overview, value: number): string {
  if (key.endsWith("Cents")) {
    return `$${(value / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  }

  return value.toLocaleString();
}

function PendingWorkersTable({
  workers,
  isLoading,
  onApprove,
  onReject,
  onIdentityStatus,
  actionsDisabled
}: {
  workers: PendingWorker[];
  isLoading: boolean;
  onApprove: (workerId: string) => void;
  onReject: (workerId: string) => void;
  onIdentityStatus: (workerId: string, status: "PENDING" | "APPROVED" | "REJECTED") => void;
  actionsDisabled: boolean;
}) {
  if (isLoading) {
    return <p className="notice">Loading pending applications...</p>;
  }

  if (workers.length === 0) {
    return <p className="notice">No pending applications.</p>;
  }

  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Photo / ID</th>
          <th>Upload</th>
          <th>ID verification</th>
          <th>Submitted</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {workers.map((worker) => (
          <tr key={worker.id}>
            <td>{worker.fullName}</td>
            <td>{worker.email}</td>
            <td>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {worker.identity?.profilePhotoUrl ? (
                  <a href={worker.identity.profilePhotoUrl} target="_blank" rel="noreferrer">
                    <img
                      src={worker.identity.profilePhotoUrl}
                      alt="Profile"
                      style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8 }}
                    />
                  </a>
                ) : (
                  <span>—</span>
                )}
                {worker.identity?.governmentIdFrontUrl ? (
                  <a href={worker.identity.governmentIdFrontUrl} target="_blank" rel="noreferrer">
                    ID front
                  </a>
                ) : (
                  <span>No ID front</span>
                )}
                {worker.identity?.governmentIdBackUrl ? (
                  <a href={worker.identity.governmentIdBackUrl} target="_blank" rel="noreferrer">
                    ID back
                  </a>
                ) : null}
              </div>
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                {worker.identity?.governmentIdType ?? worker.workerProfile?.governmentIdType ?? "—"}
              </div>
            </td>
            <td>{worker.identity?.uploadStatus ?? "—"}</td>
            <td>
              <select
                value={worker.identity?.verificationStatus ?? "PENDING"}
                disabled={actionsDisabled}
                onChange={(event) =>
                  onIdentityStatus(worker.id, event.target.value as "PENDING" | "APPROVED" | "REJECTED")
                }
              >
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </td>
            <td>{new Date(worker.createdAt).toLocaleDateString()}</td>
            <td className="actions">
              <button type="button" onClick={() => onApprove(worker.id)} disabled={actionsDisabled}>
                Approve
              </button>
              <button type="button" onClick={() => onReject(worker.id)} disabled={actionsDisabled}>
                Reject
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function App() {
  const queryClient = useQueryClient();
  const [authChecked, setAuthChecked] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [commissionInput, setCommissionInput] = useState("20");
  const [cancelFeeInput, setCancelFeeInput] = useState("25");
  const [cancelGraceInput, setCancelGraceInput] = useState("5");
  const [activeTab, setActiveTab] = useState<AdminTab>("overview");
  const [commerceSubTab, setCommerceSubTab] = useState<CommerceSubTab>("merchants");

  useEffect(() => {
    async function validateSession(): Promise<void> {
      const token = getToken();
      if (!token) {
        setAuthChecked(true);
        return;
      }

      try {
        const { user } = await apiRequest<{ user: { roles: string[] } }>("/auth/me");
        if (!user.roles.includes("ADMIN")) {
          localStorage.removeItem(TOKEN_KEY);
          setAuthenticated(false);
        } else {
          setAuthenticated(true);
        }
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    }

    void validateSession();
  }, []);

  const overviewQuery = useQuery({
    queryKey: ["admin-overview"],
    queryFn: async () => {
      const data = await apiRequest<Overview>("/admin/overview");
      if (data.cancellationFeePercent != null) {
        setCancelFeeInput(String(Math.round(data.cancellationFeePercent * 100)));
      }
      if (data.cancellationGraceMinutes != null) {
        setCancelGraceInput(String(data.cancellationGraceMinutes));
      }
      return data;
    },
    enabled: authenticated,
    retry: false
  });

  const pendingQuery = useQuery({
    queryKey: ["admin-pending-workers"],
    queryFn: () => apiRequest<{ workers: PendingWorker[] }>("/admin/workers/pending"),
    enabled: authenticated,
    retry: false
  });

  const usersQuery = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => apiRequest<{ users: AdminUser[] }>("/admin/users"),
    enabled: authenticated && activeTab === "users"
  });

  const gigsQuery = useQuery({
    queryKey: ["admin-gigs"],
    queryFn: () => apiRequest<{ gigs: AdminGig[] }>("/admin/gigs"),
    enabled: authenticated && activeTab === "gigs"
  });

  const commerceOrdersQuery = useQuery({
    queryKey: ["admin-commerce-orders"],
    queryFn: () =>
      apiRequest<{
        orders: Array<{
          id: string;
          orderNumber: number;
          status: string;
          paymentStatus: string;
          orderSource: string;
          totalCents: number;
          displayNumber?: string | number;
          parentCheckout?: {
            id: string;
            orderNumber: number;
            status: string;
            paymentStatus: string;
            totalCents: number;
          } | null;
          fulfillmentIssue?: string | null;
          waitingSince?: string;
          merchant?: { name: string };
          customer?: { fullName: string; phoneNumber: string | null };
          commerceCustomer?: { displayName: string | null; whatsappPhone: string | null };
          linkedDeliveryGig?: { id: string; status: string; assignedWorkerId?: string | null } | null;
          merchantNotification?: {
            status: string;
            label: string | null;
            attemptCount: number;
            lastErrorCategory: string | null;
            providerMessageSid: string | null;
            deliveredAt: string | null;
          } | null;
          courierRelease?: {
            reason: string;
            stage: string;
            outcome: string;
            createdAt: string;
          } | null;
          createdAt: string;
        }>;
      }>("/admin/commerce/orders"),
    enabled: authenticated && activeTab === "commerce"
  });

  const unlistedQuery = useQuery({
    queryKey: ["admin-unlisted-requests"],
    queryFn: () =>
      apiRequest<{
        requests: Array<{
          id: string;
          requestNumber: number;
          status: string;
          originalRequestText: string;
          parsedItemName: string;
          quantity: number;
          optionalMaxBudgetCents: number | null;
          foundProductName: string | null;
          foundPriceCents: number | null;
          foundMerchantName: string | null;
          deliveryLabel: string | null;
          totalCents: number | null;
          customer: { displayName: string | null; whatsappPhone: string | null } | null;
          courier: { id: string; fullName: string } | null;
          linkedDeliveryGigId: string | null;
          events: Array<{ type: string; createdAt: string }>;
          paymentAttempts: Array<{ status: string; amountCents: number; provider: string }>;
          createdAt: string;
          updatedAt: string;
        }>;
      }>("/admin/commerce/unlisted-requests"),
    enabled: authenticated && activeTab === "commerce" && commerceSubTab === "unlisted"
  });

  const approveMutation = useMutation({
    mutationFn: (workerId: string) => apiRequest(`/admin/workers/${workerId}/approve`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-pending-workers"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-overview"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    }
  });

  const rejectMutation = useMutation({
    mutationFn: (workerId: string) =>
      apiRequest(`/admin/workers/${workerId}/reject`, { method: "POST", body: JSON.stringify({}) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-pending-workers"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-overview"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    }
  });

  const commissionMutation = useMutation({
    mutationFn: () => apiRequest("/admin/commission", { method: "POST", body: JSON.stringify({ rate: Number(commissionInput) / 100 }) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin-overview"] })
  });

  const identityStatusMutation = useMutation({
    mutationFn: ({ workerId, status }: { workerId: string; status: "PENDING" | "APPROVED" | "REJECTED" }) =>
      apiRequest(`/admin/workers/${workerId}/identity-status`, {
        method: "POST",
        body: JSON.stringify({ status })
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin-pending-workers"] })
  });

  const retryWhatsAppMutation = useMutation({
    mutationFn: (orderId: string) =>
      apiRequest(`/admin/commerce/orders/${orderId}/retry-whatsapp`, { method: "POST", body: JSON.stringify({}) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin-commerce-orders"] })
  });

  const cancellationSettingsMutation = useMutation({
    mutationFn: () =>
      apiRequest("/admin/settings/cancellation", {
        method: "PATCH",
        body: JSON.stringify({
          cancellationFeePercent: Number(cancelFeeInput) / 100,
          cancellationGraceMinutes: Number(cancelGraceInput)
        })
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin-overview"] })
  });

  if (!authChecked) {
    return (
      <main className="login-layout">
        <p className="notice">Checking admin session...</p>
      </main>
    );
  }

  if (!authenticated) {
    return (
      <main className="login-layout">
        <LoginPanel onSuccess={() => setAuthenticated(true)} />
      </main>
    );
  }

  const overview =
    overviewQuery.data ??
    ({
      users: 0,
      workers: 0,
      pendingWorkers: 0,
      openGigs: 0,
      completedGigs: 0,
      grossVolumeCents: 0,
      platformRevenueCents: 0,
      commissionRate: 0.2
    } satisfies Overview);

  const pendingWorkers = pendingQuery.data?.workers ?? [];
  const pendingCount = overview.pendingWorkers || pendingWorkers.length;

  return (
    <main>
      <aside>
        <div className="brand">
          <img className="brand-logo" src="/logo.png" alt="DUTS" />
        </div>
        <div className="brand-mark" aria-hidden="true" />
        <nav>
          {(
            [
              ["Overview", "overview"],
              ["Pending couriers", "pending"],
              ["Users", "users"],
              ["Commerce", "commerce"],
              ["Local Help", "gigs"]
            ] as const
          ).map(([label, tab]) => (
            <button key={tab} type="button" className={activeTab === tab ? "nav-active" : ""} onClick={() => setActiveTab(tab)}>
              {label}
              {tab === "pending" && pendingCount > 0 ? <span className="nav-badge">{pendingCount}</span> : null}
            </button>
          ))}
        </nav>
        <button
          type="button"
          className="sign-out"
          onClick={() => {
            localStorage.removeItem(TOKEN_KEY);
            setAuthenticated(false);
          }}
        >
          Sign out
        </button>
      </aside>
      <section className="content">
        <header>
          <div>
            <p className="eyebrow">{activeTab === "commerce" ? "DUTS" : "Operations dashboard"}</p>
            <h1>{activeTab === "commerce" ? "Commerce" : "Marketplace command center"}</h1>
            {activeTab === "commerce" ? (
              <p className="muted commerce-subhead">Manage shops, products and orders.</p>
            ) : null}
          </div>
          {activeTab !== "commerce" ? (
            <div className="commission">{Math.round(overview.commissionRate * 100)}% commission</div>
          ) : null}
        </header>

        {overviewQuery.error ? <p className="notice">{overviewQuery.error.message}</p> : null}

        {pendingCount > 0 && activeTab === "overview" ? (
          <section className="panel alert-panel">
            <h2>{pendingCount} worker application{pendingCount === 1 ? "" : "s"} awaiting review</h2>
            <p>Review and approve couriers so they can go online and accept deliveries.</p>
            <button type="button" onClick={() => setActiveTab("pending")}>
              Review pending workers
            </button>
          </section>
        ) : null}

        {activeTab === "overview" ? (
          <>
            <div className="grid">
              {cards.map(([label, key]) => (
                <article key={key} className={key === "pendingWorkers" && overview.pendingWorkers > 0 ? "highlight-card" : ""}>
                  <span>{label}</span>
                  <strong>{formatValue(key, overview[key])}</strong>
                </article>
              ))}
            </div>

            {pendingWorkers.length > 0 ? (
              <section className="panel">
                <h2>Pending worker applications</h2>
                {approveMutation.error ? <p className="notice">{approveMutation.error.message}</p> : null}
                {rejectMutation.error ? <p className="notice">{rejectMutation.error.message}</p> : null}
                <PendingWorkersTable
                  workers={pendingWorkers}
                  isLoading={pendingQuery.isLoading}
                  onApprove={(workerId) => approveMutation.mutate(workerId)}
                  onReject={(workerId) => rejectMutation.mutate(workerId)}
                  onIdentityStatus={(workerId, status) => identityStatusMutation.mutate({ workerId, status })}
                  actionsDisabled={
                    approveMutation.isPending || rejectMutation.isPending || identityStatusMutation.isPending
                  }
                />
              </section>
            ) : null}

            <section className="panel">
              <h2>Commission settings</h2>
              <div className="commission-form">
                <input type="number" min={5} max={35} value={commissionInput} onChange={(event) => setCommissionInput(event.target.value)} />
                <span>% platform fee</span>
                <button type="button" onClick={() => commissionMutation.mutate()} disabled={commissionMutation.isPending}>
                  {commissionMutation.isPending ? "Saving..." : "Update commission"}
                </button>
              </div>
            </section>

            <section className="panel">
              <h2>Cancellation policy</h2>
              <div className="commission-form">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={cancelFeeInput}
                  onChange={(event) => setCancelFeeInput(event.target.value)}
                />
                <span>% of gig total after grace (100% to worker)</span>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={cancelGraceInput}
                  onChange={(event) => setCancelGraceInput(event.target.value)}
                />
                <span>grace minutes</span>
                <button
                  type="button"
                  onClick={() => cancellationSettingsMutation.mutate()}
                  disabled={cancellationSettingsMutation.isPending}
                >
                  {cancellationSettingsMutation.isPending ? "Saving..." : "Update cancellation policy"}
                </button>
              </div>
            </section>
          </>
        ) : null}

        {activeTab === "pending" ? (
          <section className="panel">
            <h2>Pending worker applications</h2>
            {pendingQuery.error ? <p className="notice">{pendingQuery.error.message}</p> : null}
            {approveMutation.error ? <p className="notice">{approveMutation.error.message}</p> : null}
            {rejectMutation.error ? <p className="notice">{rejectMutation.error.message}</p> : null}
            <PendingWorkersTable
              workers={pendingWorkers}
              isLoading={pendingQuery.isLoading}
              onApprove={(workerId) => approveMutation.mutate(workerId)}
              onReject={(workerId) => rejectMutation.mutate(workerId)}
              onIdentityStatus={(workerId, status) => identityStatusMutation.mutate({ workerId, status })}
              actionsDisabled={
                approveMutation.isPending || rejectMutation.isPending || identityStatusMutation.isPending
              }
            />
          </section>
        ) : null}

        {activeTab === "users" ? (
          <section className="panel">
            <h2>All users</h2>
            {usersQuery.error ? <p className="notice">{usersQuery.error.message}</p> : null}
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {(usersQuery.data?.users ?? []).map((user) => (
                  <tr key={user.id}>
                    <td>{user.fullName}</td>
                    <td>{user.email}</td>
                    <td>{user.roles.join(", ")}</td>
                    <td>{user.accountStatus}</td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        {activeTab === "gigs" ? (
          <section className="panel">
            <h2>Local Help jobs</h2>
            <p className="notice" style={{ marginBottom: 12 }}>
              Legacy Local Help listings. Marketplace shopping uses Commerce → Orders.
            </p>
            {gigsQuery.error ? <p className="notice">{gigsQuery.error.message}</p> : null}
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Posted</th>
                </tr>
              </thead>
              <tbody>
                {(gigsQuery.data?.gigs ?? []).map((gig) => (
                  <tr key={gig.id}>
                    <td>{gig.title}</td>
                    <td>{gig.client?.fullName ?? "—"}</td>
                    <td>{gig.status}</td>
                    <td>{new Date(gig.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        {activeTab === "commerce" ? (
          <>
            <div className="commerce-home-grid">
              {(
                [
                  ["Merchants", "merchants", "Shops and product catalogs"],
                  ["Orders", "orders", "Customer shop orders"],
                  ["DUTS Catalog", "catalog", "Shared product photos & details"],
                  ["Deliveries", "deliveries", "Courier-linked shop orders"],
                  ["Unlisted items", "unlisted", "Catalog-missing search requests"]
                ] as const
              ).map(([label, id, hint]) => (
                <button
                  key={id}
                  type="button"
                  className={commerceSubTab === id ? "commerce-home-card active" : "commerce-home-card"}
                  onClick={() => setCommerceSubTab(id)}
                >
                  <strong>{label}</strong>
                  <span>{hint}</span>
                </button>
              ))}
            </div>

            {commerceSubTab === "merchants" ? <CommercePilotPanel apiRequest={apiRequest} /> : null}
            {commerceSubTab === "catalog" ? <DutsCatalogPanel apiRequest={apiRequest} /> : null}
            {commerceSubTab === "orders" || commerceSubTab === "deliveries" ? (
              <section className="panel commerce-panel">
                <h2>{commerceSubTab === "deliveries" ? "Deliveries" : "Orders"}</h2>
                {commerceOrdersQuery.error ? <p className="notice">{commerceOrdersQuery.error.message}</p> : null}
                <div className="order-card-list">
                  {(commerceOrdersQuery.data?.orders ?? [])
                    .filter((o) =>
                      commerceSubTab === "deliveries" ? Boolean(o.linkedDeliveryGig) : true
                    )
                    .map((o) => (
                      <article key={o.id} className="order-card">
                        <div className="order-card-top">
                          <strong>
                            Order #{typeof o.displayNumber === "string" || typeof o.displayNumber === "number" ? o.displayNumber : o.orderNumber}
                          </strong>
                          <span>${(o.totalCents / 100).toFixed(2)}</span>
                        </div>
                        {o.parentCheckout ? (
                          <p className="muted">
                            Parent #{o.parentCheckout.orderNumber} · {String(o.parentCheckout.status).replace(/_/g, " ")}
                          </p>
                        ) : null}
                        <p className="muted">{o.merchant?.name ?? "Shop"}</p>
                        <p className="muted">
                          {o.customer?.fullName ?? "Customer"}
                          {o.customer?.phoneNumber ? ` · ${o.customer.phoneNumber}` : ""}
                        </p>
                        <p>
                          <span className="status-badge tone-muted">{String(o.status).replace(/_/g, " ")}</span>
                          {o.fulfillmentIssue ? (
                            <span className="status-badge" style={{ marginLeft: 8 }}>
                              {o.fulfillmentIssue}
                            </span>
                          ) : null}
                        </p>
                        <p className="muted">Payment: {String(o.paymentStatus).replace(/_/g, " ")}</p>
                        {o.merchantNotification?.label ? (
                          <p className="muted">{o.merchantNotification.label}</p>
                        ) : (
                          <p className="muted">Merchant notification pending</p>
                        )}
                        {o.courierRelease ? (
                          <p className="muted">
                            {o.courierRelease.outcome === "NEEDS_ATTENTION"
                              ? "Courier unable to continue"
                              : "Courier released delivery"}
                            {" · "}
                            {o.courierRelease.stage === "AFTER_PICKUP" ? "After pickup" : "Before pickup"}
                            {" · "}
                            {o.courierRelease.reason.replace(/_/g, " ").toLowerCase()}
                            {o.courierRelease.outcome === "RELEASED" ? " · Finding replacement courier" : " · Needs attention"}
                          </p>
                        ) : null}
                        {o.orderNumber !== 2 && o.merchantNotification?.status !== "DELIVERED" ? (
                          <button
                            type="button"
                            className="secondary"
                            disabled={retryWhatsAppMutation.isPending}
                            onClick={() => retryWhatsAppMutation.mutate(o.id)}
                          >
                            Retry WhatsApp
                          </button>
                        ) : null}
                        {o.waitingSince ? (
                          <p className="muted">Waiting since {new Date(o.waitingSince).toLocaleString()}</p>
                        ) : null}
                        {o.linkedDeliveryGig ? (
                          <p className="muted">Delivery: {o.linkedDeliveryGig.status}</p>
                        ) : commerceSubTab === "orders" ? (
                          <p className="muted">No courier linked yet</p>
                        ) : null}
                      </article>
                    ))}
                </div>
                {commerceSubTab === "deliveries" &&
                !(commerceOrdersQuery.data?.orders ?? []).some((o) => o.linkedDeliveryGig) ? (
                  <p className="muted">No deliveries linked yet.</p>
                ) : null}
              </section>
            ) : null}
            {commerceSubTab === "unlisted" ? (
              <section className="panel commerce-panel">
                <h2>Unlisted item requests</h2>
                {unlistedQuery.error ? <p className="notice">{unlistedQuery.error.message}</p> : null}
                <div className="order-card-list">
                  {(unlistedQuery.data?.requests ?? []).map((r) => (
                    <article key={r.id} className="order-card">
                      <div className="order-card-top">
                        <strong>
                          #{r.requestNumber} · {r.status.replace(/_/g, " ")}
                        </strong>
                        <span>{r.totalCents != null ? `$${(r.totalCents / 100).toFixed(2)}` : "—"}</span>
                      </div>
                      <p>{r.parsedItemName} × {r.quantity}</p>
                      <p className="muted">{r.originalRequestText}</p>
                      {r.foundProductName ? (
                        <p>
                          Found: {r.foundProductName}
                          {r.foundPriceCents != null ? ` · $${(r.foundPriceCents / 100).toFixed(2)}` : ""}
                          {r.foundMerchantName ? ` · ${r.foundMerchantName}` : ""}
                        </p>
                      ) : null}
                      <p className="muted">
                        {r.customer?.displayName ?? r.customer?.whatsappPhone ?? "Customer"}
                        {r.courier ? ` · Courier ${r.courier.fullName}` : ""}
                      </p>
                      {r.optionalMaxBudgetCents != null ? (
                        <p className="muted">Max budget ${(r.optionalMaxBudgetCents / 100).toFixed(2)}</p>
                      ) : null}
                      {r.linkedDeliveryGigId ? <p className="muted">Delivery gig {r.linkedDeliveryGigId}</p> : null}
                      <p className="muted">
                        {new Date(r.createdAt).toLocaleString()} · events: {r.events.map((e) => e.type).join(" → ")}
                      </p>
                    </article>
                  ))}
                </div>
                {(unlistedQuery.data?.requests ?? []).length === 0 ? (
                  <p className="muted">No unlisted-item requests yet.</p>
                ) : null}
              </section>
            ) : null}
          </>
        ) : null}
      </section>
    </main>
  );
}
